// disabled to fix circular dependencies, these should be moved
// to lower level packages instead in the future
/* eslint-disable webflow/module-top-level-imports */
import {
  type DeconstructedRow,
  type Type,
  withSystem,
  TGeneric as Generic,
  TElement as Element,
  TUnknown as Unknown,
  TRigid as Rigid,
  TTypeReference as TypeReference,
  TTypeApplication as TypeApplication,
  TTypeConstructor as TypeConstructor,
  TRowEmpty as RowEmpty,
  TRowCons as RowCons,
} from '@packages/systems/wfdl/DataTypeDefinition';
import {name as pluginName} from '@packages/systems/wfdl/Plugins/Names';
/* eslint-enable webflow/module-top-level-imports */
import {emptyObject} from '@packages/utilities/fp/utils';
import {
  type Qualified,
  type TypeName,
  typeName,
  qualify,
} from '@packages/systems/wfdl-Qualified';

export const TGeneric = Generic;
export const TElement = Element;
export const TUnknown = Unknown;
export const TRigid = Rigid;
export const TTypeReference = TypeReference;
export const TTypeApplication = TypeApplication;
export const TTypeConstructor = TypeConstructor;
export const TRowEmpty = RowEmpty;
export const TRowCons = RowCons;

export const constructTypeApplication = ({
  con,
  args,
}: {
  con: Qualified<TypeName>;
  args: Array<Type>;
}): Type =>
  args.reduce(
    (res, arg) => TTypeApplication({con: res, arg}),
    TTypeConstructor(con)
  );

export const constructRow = (row: DeconstructedRow): Type =>
  Object.keys(row.items).reduce(
    (tail, label) =>
      TRowCons({
        label,
        // @ts-expect-error - TS2322 - Type 'Type | undefined' is not assignable to type 'Type'.
        type: row.items[label],
        tail,
      }),
    row.tail
  );
export const a = TTypeReference('a');
export const b = TTypeReference('b');
export const c = TTypeReference('c');
export const r = TTypeReference('r');

/**
 * Creators via `Builtin` Plugin
 */
const builtinName = pluginName('Builtin');
const builtin = (name: TypeName): Qualified<TypeName> =>
  qualify(builtinName, name);

// Function Type
export const functionTypeName = builtin(typeName('Function'));
export const tyFunction = TTypeConstructor(functionTypeName);
export const functionOf = (arg: Type, ret: Type): Type =>
  TTypeApplication({con: TTypeApplication({con: tyFunction, arg}), arg: ret});

// Boolean Type
export const booleanTypeName = builtin(typeName('Boolean'));

/** @deprecated: Use TBoolean from @packages/systems/plugins-utils/Boolean. */
export const TBoolean = TTypeConstructor(booleanTypeName);

// Number type
export const numberTypeName = builtin(typeName('Number'));
export const TNumber = TTypeConstructor(numberTypeName);

// Text Type
export const textTypeName = builtin(typeName('Text'));
export const TText = TTypeConstructor(textTypeName);

// List type
export const listTypeName = builtin(typeName('List'));
export const tyList = TTypeConstructor(listTypeName);
export const TList = (arg: Type): Type => TTypeApplication({con: tyList, arg});
export const TListOfElement = TList(TElement);

// Element type
export const elementTypeName = builtin(typeName('Element'));
export const tyElement = TTypeConstructor(elementTypeName);
export const TElementOf = (context: Type, state: Type): Type =>
  TTypeApplication({
    con: TTypeApplication({con: tyElement, arg: context}),
    arg: state,
  });

// Record Type
export const recordTypeName = builtin(typeName('Record'));
export const tyRecord = TTypeConstructor(recordTypeName);
export const recordOf = (row: Type): Type =>
  TTypeApplication({con: tyRecord, arg: row});

// Internal helper function for TRecord and TUnion constructor only
const fieldsToRow = (
  fields: {
    [key: string]: Type;
  },
  tail: Type
): Type =>
  Object.keys(fields).reduce(
    (row, label) =>
      TRowCons({
        label,
        // @ts-expect-error - TS2322 - Type 'Type | undefined' is not assignable to type 'Type'.
        type: fields[label],
        tail: row,
      }),
    tail
  );
/**
 * Function for creating record types in an ergonomic way.
 * Omitting the tail to creates a closed record (a record that has exactly
 * the specified fields).
 * For example, creating a record with 2 properties `x` and `y`:
 * ```
 * TRecord({x: TNumber, y: {type: 'Text'}})
 * ```
 */
export const TRecord = (
  fields: {
    [key: string]: Type;
  },
  tail: Type = TRowEmpty
): Type => recordOf(fieldsToRow(fields, tail));

/**
 * The type of the empty record.
 */
export const TEmptyRecord = TRecord(emptyObject);

// Union Type
export const unionTypeName = builtin(typeName('Union'));
export const tyUnion = TTypeConstructor(unionTypeName);
export const unionOf = (row: Type): Type =>
  TTypeApplication({con: tyUnion, arg: row});

/**
 * Function for creating a closed union type in an ergonomic way.
 * For example, creating a union for representing an optional number:
 * ```
 * TUnion({None: TEmptyRecord, Some: TNumber})
 * ```
 */
export const TUnion = (
  fields: {
    [key: string]: Type;
  },
  tail: Type = TRowEmpty
): Type => unionOf(fieldsToRow(fields, tail));

/**
 * Function for creating a closed union type with labels that differ from the value.
 * For example, creating a union for representing an optional number:
 * ```
 * TUnionWithLabels({None: {label: 'None', type: TEmptyRecord}, Some: {label: 'Some Other Thing', type: TNumber}})
 * ```
 */
export const TUnionWithLabels = (fields: {
  [key: string]: {
    label: string;
    type: Type;
  };
}): Type =>
  unionOf(
    Object.entries(fields).reduce(
      (row, [key, {label, type}]: [any, any]) =>
        withSystem(
          {label},
          TRowCons({
            label: key,
            type,
            tail: row,
          })
        ),
      TRowEmpty
    )
  );

// Enum Type
export const enumTypeName = builtin(typeName('Enum'));
export const tyEnum = TTypeConstructor(enumTypeName);
export const enumOf = (row: Type): Type =>
  TTypeApplication({con: tyEnum, arg: row});

/**
 * Function for creating a closed enum type in an ergonomic way.
 * For example, creating an enum of sizes `small`, `medium` and `large`,
 * with human-readable labels of "Small", "Medium", and "Large":
 * ```
 * TEnum({
     small: 'Small',
     medium: 'Medium,
     large: 'Large'
   })
 * ```
 * @deprecated: Use `TUnion` with empty records as values.
 */
export const TEnum = (values: {[key: string]: string}): Type =>
  enumOf(
    Object.entries(values).reduce(
      (row, [key, label]: [any, any]) =>
        withSystem(
          {label},
          TRowCons({
            label: key,
            type: TEmptyRecord,
            tail: row,
          })
        ),
      TRowEmpty
    )
  );

export const TNoDataTypeProvided = TRecord(emptyObject);
